import { useState, useEffect, useRef, useCallback } from "react";
import { WA } from "@/lib/whatsapp";

const SESSION_KEYS = {
  DISMISSED: "dms_wa_popup_dismissed",
  OPENED: "dms_wa_popup_opened",
  CLICKED: "dms_wa_clicked",
};

/**
 * Dispatch analytics events safely to GA4, Meta Pixel, or custom listeners.
 */
const trackAnalytics = (eventName, params = {}) => {
  if (typeof window !== "undefined") {
    // Google Analytics 4 (gtag) integration point
    if (typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }
    // Meta Pixel / fbq integration point
    if (typeof window.fbq === "function") {
      window.fbq("trackCustom", eventName, params);
    }
    // Custom window event for project listeners
    window.dispatchEvent(
      new CustomEvent("dms_analytics", {
        detail: { eventName, params },
      })
    );
  }
};

export function useWhatsAppPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const startTimeRef = useRef(Date.now());
  const activeTimeRef = useRef(0);
  const lastActiveTimestampRef = useRef(Date.now());
  const timerIdRef = useRef(null);
  const isTriggeredRef = useRef(false);

  // Check if conditions permit showing popup
  const shouldSuppress = useCallback(() => {
    if (typeof window === "undefined") return true;
    try {
      const dismissed = sessionStorage.getItem(SESSION_KEYS.DISMISSED) === "true";
      const opened = sessionStorage.getItem(SESSION_KEYS.OPENED) === "true";
      const clicked = sessionStorage.getItem(SESSION_KEYS.CLICKED) === "true";
      return dismissed || opened || clicked;
    } catch {
      return false;
    }
  }, []);

  // Trigger popup action
  const triggerPopup = useCallback((triggerType) => {
    if (isTriggeredRef.current || shouldSuppress()) return;

    isTriggeredRef.current = true;
    try {
      sessionStorage.setItem(SESSION_KEYS.OPENED, "true");
    } catch (e) {
      // Ignore storage errors in private browsing edge cases
    }

    const timeBeforeInteraction = Math.round(
      (Date.now() - startTimeRef.current) / 1000
    );

    setIsOpen(true);
    trackAnalytics("wa_popup_shown", {
      trigger_type: triggerType,
      time_to_trigger_seconds: timeBeforeInteraction,
    });
  }, [shouldSuppress]);

  // Handle active timing & scroll listener
  useEffect(() => {
    if (shouldSuppress()) return;

    const TARGET_ACTIVE_MS = 5500; // 5.5 seconds of active time

    // Timer resume/pause based on tab visibility
    const updateActiveTime = () => {
      const now = Date.now();
      if (!document.hidden) {
        activeTimeRef.current += now - lastActiveTimestampRef.current;
      }
      lastActiveTimestampRef.current = now;
    };

    const checkTimer = () => {
      if (document.hidden) return;
      const now = Date.now();
      const currentActive =
        activeTimeRef.current + (now - lastActiveTimestampRef.current);

      if (currentActive >= TARGET_ACTIVE_MS) {
        triggerPopup("timer");
        if (timerIdRef.current) clearInterval(timerIdRef.current);
      }
    };

    timerIdRef.current = setInterval(checkTimer, 300);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab went inactive: accumulate active time up to now
        activeTimeRef.current += Date.now() - lastActiveTimestampRef.current;
      } else {
        // Tab resumed: reset timestamp reference
        lastActiveTimestampRef.current = Date.now();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Scroll depth trigger (> 40% scroll depth)
    let rafId = null;
    const handleScroll = () => {
      if (isTriggeredRef.current) return;
      if (rafId) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const scrollHeight =
          document.documentElement.scrollHeight - window.innerHeight;

        if (scrollHeight > 0) {
          const scrollPercent = (scrollTop / scrollHeight) * 100;
          if (scrollPercent >= 40) {
            triggerPopup("scroll");
            if (timerIdRef.current) clearInterval(timerIdRef.current);
          }
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (timerIdRef.current) clearInterval(timerIdRef.current);
      if (rafId) cancelAnimationFrame(rafId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [shouldSuppress, triggerPopup]);

  // Global listener: If user clicks ANY WhatsApp button anywhere on the page, remember it & prevent popup
  useEffect(() => {
    const handleGlobalClick = (event) => {
      const target = event.target;
      if (!target || typeof target.closest !== "function") return;

      const link = target.closest("a, button");
      if (!link) return;

      const href = link.getAttribute("href") || "";
      const testId = link.getAttribute("data-testid") || "";
      const isWhatsAppLink =
        href.includes("wa.me") ||
        href.includes("whatsapp") ||
        testId.toLowerCase().includes("whatsapp");

      if (isWhatsAppLink) {
        try {
          sessionStorage.setItem(SESSION_KEYS.CLICKED, "true");
          sessionStorage.setItem(SESSION_KEYS.DISMISSED, "true");
        } catch (e) {
          // ignore
        }
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleGlobalClick, { capture: true });
    return () => {
      document.removeEventListener("click", handleGlobalClick, { capture: true });
    };
  }, []);

  // Dismiss popup
  const dismissPopup = useCallback(() => {
    try {
      sessionStorage.setItem(SESSION_KEYS.DISMISSED, "true");
    } catch (e) {
      // ignore
    }
    const timeBeforeInteraction = Math.round(
      (Date.now() - startTimeRef.current) / 1000
    );
    trackAnalytics("wa_popup_closed", {
      time_before_interaction_seconds: timeBeforeInteraction,
    });
    setIsOpen(false);
  }, []);

  // Primary WhatsApp action click
  const handleWhatsAppClick = useCallback(() => {
    try {
      sessionStorage.setItem(SESSION_KEYS.CLICKED, "true");
      sessionStorage.setItem(SESSION_KEYS.DISMISSED, "true");
    } catch (e) {
      // ignore
    }
    const timeBeforeInteraction = Math.round(
      (Date.now() - startTimeRef.current) / 1000
    );
    trackAnalytics("wa_popup_whatsapp_clicked", {
      time_before_interaction_seconds: timeBeforeInteraction,
    });
    setIsOpen(false);
    return WA.popup();
  }, []);

  return {
    isOpen,
    dismissPopup,
    handleWhatsAppClick,
  };
}
